<script setup>
import { computed, defineProps } from "vue";
import { shortDate } from "@/utils/format";

// A headline number with its last 30 days drawn underneath, and how this week
// compares with the week before
const props = defineProps({
  title: { type: String, required: true },
  value: { type: String, required: true },
  note: { type: String, default: "" },
  // Every day of the last 30, oldest first: [{ x: "2026-09-29", y: 4 }]
  series: { type: Array, required: true },
  format: { type: Function, required: true },
  thisWeek: { type: String, default: "" },
  change: { type: Number, default: null },
  // Whether a rise is good news (true), bad news (false) or neither (null)
  goodWhenUp: { type: Boolean, default: null },
});

const isDark =
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;
const surface = isDark ? "#231F42" : "#FFFFFF";

const chartSeries = computed(() => [
  { name: props.title, data: props.series.map((point) => point.y) },
]);

const options = computed(() => ({
  chart: {
    type: "area",
    sparkline: { enabled: true },
    animations: { enabled: false },
    fontFamily: "inherit",
  },
  colors: ["#CF5AE2"],
  stroke: { curve: "straight", width: 2, lineCap: "round" },
  fill: { type: "solid", opacity: 0.1 },
  markers: {
    size: 0,
    strokeColors: surface,
    strokeWidth: 2,
    hover: { size: 5 },
  },
  yaxis: { min: 0 },
  xaxis: {
    crosshairs: {
      show: true,
      stroke: { color: isDark ? "#4A4570" : "#D9D6E4", width: 1 },
    },
  },
  tooltip: {
    // Only our own numbers and dates go in here
    custom: ({ dataPointIndex }) => {
      const point = props.series[dataPointIndex];
      if (!point) return "";
      return `<div class="trend-tip"><strong>${props.format(
        point.y
      )}</strong><span>${shortDate(point.x)}</span></div>`;
    },
  },
}));

const direction = computed(() => {
  if (props.change === null || Math.round(props.change) === 0) return "flat";
  return props.change > 0 ? "up" : "down";
});
const changeClass = computed(() => {
  if (direction.value === "flat" || props.goodWhenUp === null)
    return "text-secondary dark:text-night-muted";
  const good = (direction.value === "up") === props.goodWhenUp;
  return good ? "text-up dark:text-up-dark" : "text-down dark:text-down-dark";
});
const changeText = computed(() => {
  if (props.change === null) return "Nothing the week before";
  if (direction.value === "flat") return "Same as the week before";
  return `${direction.value === "up" ? "Up" : "Down"} ${Math.abs(
    Math.round(props.change)
  )}% on the week before`;
});
</script>

<template>
  <section class="card flex h-full flex-col p-5">
    <div class="flex items-baseline justify-between gap-3">
      <h2 class="card-note font-medium">{{ title }}</h2>
      <span v-if="note" class="text-xs text-secondary dark:text-night-muted">{{
        note
      }}</span>
    </div>
    <p
      class="mt-1 text-4xl font-semibold tracking-tight text-primary dark:text-white"
    >
      {{ value }}
    </p>
    <p class="mt-1 text-sm text-secondary dark:text-night-muted">
      <span class="font-medium text-primary dark:text-white">{{
        thisWeek
      }}</span>
      in the last 7 days
    </p>
    <p class="text-sm font-medium" :class="changeClass">
      <font-awesome-icon
        v-if="direction !== 'flat' && change !== null"
        :icon="direction === 'up' ? 'arrow-up' : 'arrow-down'"
        class="mr-1 text-xs"
      />{{ changeText }}
    </p>
    <div class="-mx-1 mt-auto pt-4">
      <apexchart
        type="area"
        height="72"
        :options="options"
        :series="chartSeries"
      />
    </div>
    <div
      class="mt-1 flex justify-between text-xs text-secondary dark:text-night-muted"
      aria-hidden="true"
    >
      <span>30 days ago</span>
      <span>Today</span>
    </div>
  </section>
</template>
